import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { api } from '../../services/api';
import { fmtDate, categoryColor } from './format';
import { c, t, tbl } from '../../theme';
import { useSort, sortRows, SortTh } from '../Shared/sortable';
import ExpandableScroll from '../Shared/ExpandableScroll';
import { useRowLimit, ShowMoreRow } from '../Shared/rowLimit';
import NotesCell from '../Shared/NotesCell';

// Matches the real per-facility "NEW HIRES" table: Employee Name (ID), Job
// Title, Supervisor, Phone Number, Category, Start Date, 90 Day, Pre-Hire,
// Week 1-4, Notes, English or Spanish.
//
// Week 1-4 are CALCULATED milestones (today vs Start Date — see
// backend/src/routes/staffing.js computeOnboardingWeeks), not manually
// ticked. Phone Number, Pre-Hire, 90 Day, Notes and English/Spanish are all
// Recruiting-owned (staffing_new_hire_notes, migrations 019/111) and
// editable here when canEdit is true — Pre-Hire and 90 Day are deliberately
// NOT calculated like Week 1-4: they're a real manual checkbox, per Jesse.
export default function NewHireTable({ newHires, canEdit = false }) {
  const showPeople = newHires.some((h) => 'worker_name' in h);
  const sort = useSort();
  const sorted = sortRows(newHires, sort.key, sort.dir, {
    worker_name: (h) => h.worker_name || '',
    job_profile: (h) => h.job_profile || '',
    manager:     (h) => h.manager || '',
    category:    (h) => h.job_requisition_category || '',
    start_date:  (h) => h.start_date || '',
  });
  const limiter = useRowLimit(sorted);
  return (
    <ExpandableScroll collapsedHeight={520}>
      <table style={s.table}>
        <thead>
          <tr>
            {showPeople && <SortTh label="Employee Name (ID)" sortKey="worker_name" sort={sort} sticky style={{ ...tbl.th, padding: '9px 14px 9px 11px' }} />}
            <SortTh label="Job Profile" sortKey="job_profile" sort={sort} sticky style={showPeople ? undefined : { ...tbl.th, padding: '9px 14px 9px 11px' }} />
            <SortTh label="Supervisor" sortKey="manager" sort={sort} sticky />
            {showPeople && <SortTh label="Phone Number" sticky />}
            <SortTh label="Category" sortKey="category" sort={sort} sticky />
            <SortTh label="Start Date" sortKey="start_date" sort={sort} sticky />
            {['90 Day', 'Pre-Hire', 'Week 1', 'Week 2', 'Week 3', 'Week 4', 'Notes', 'English or Spanish'].map((h) => (
              <SortTh key={h} label={h} sticky />
            ))}
          </tr>
        </thead>
        <tbody>
          {limiter.visible.map((h, i) => (
            <NewHireRow key={h.id} hire={h} canEdit={canEdit} showPeople={showPeople} index={i} />
          ))}
          <ShowMoreRow limiter={limiter} colSpan={showPeople ? 14 : 12} />
          {!newHires.length && (
            <tr><td colSpan={showPeople ? 14 : 12} style={s.empty}>No new hires in the last 35 days.</td></tr>
          )}
        </tbody>
      </table>
    </ExpandableScroll>
  );
}

function NewHireRow({ hire: h, canEdit, showPeople, index }) {
  const qc = useQueryClient();
  const [notes, setNotes] = useState(h.notes ?? '');
  const [language, setLanguage] = useState(h.language ?? '');
  const [phoneNumber, setPhoneNumber] = useState(h.phone_number ?? '');

  // Re-sync on prop change, not just on mount — see VacancyTable.jsx's
  // VacancyRow for why (same row id can be reused across the "all cost
  // centers" list and a focused cost center's own list).
  useEffect(() => {
    setNotes(h.notes ?? '');
    setLanguage(h.language ?? '');
    setPhoneNumber(h.phone_number ?? '');
  }, [h.notes, h.language, h.phone_number]);

  const saveMutation = useMutation({
    mutationFn: (payload) => api.patch(`/staffing/new-hires/${h.worker_employee_id}/notes`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staffing-new-hires'] });
      qc.invalidateQueries({ queryKey: ['staffing-costcenter'] });
    },
  });

  // Every save upserts the whole Recruiting-owned row, so every field not
  // being changed right now has to be re-sent as-is, same reason
  // VacancyTable.jsx's basePayload() exists — otherwise whichever field was
  // last saved elsewhere gets silently cleared.
  const basePayload = () => ({
    notes: h.notes ?? null,
    language: h.language ?? null,
    phone_number: h.phone_number ?? null,
    pre_hire: h.pre_hire ?? null,
    ninety_day: h.ninety_day ?? null,
  });

  function saveNotes() {
    if (notes === (h.notes ?? '')) return;
    saveMutation.mutate({ ...basePayload(), notes: notes || null });
  }
  function saveLanguage(next) {
    setLanguage(next);
    saveMutation.mutate({ ...basePayload(), language: next || null });
  }
  function savePhoneNumber() {
    if (phoneNumber === (h.phone_number ?? '')) return;
    saveMutation.mutate({ ...basePayload(), phone_number: phoneNumber || null });
  }
  function togglePreHire() {
    saveMutation.mutate({ ...basePayload(), pre_hire: !h.pre_hire });
  }
  function toggleNinetyDay() {
    saveMutation.mutate({ ...basePayload(), ninety_day: !h.ninety_day });
  }

  const canEditThisRow = canEdit && !!h.worker_employee_id;
  const catC = categoryColor(h.job_requisition_category);

  const firstBorder = { borderLeft: `3px solid ${catC.border}`, padding: '9px 14px 9px 11px' };

  return (
    <tr style={tbl.trZebra(index)}>
      {showPeople && (
        <td style={{ ...tbl.td, ...firstBorder }}>{h.worker_name}{h.worker_employee_id ? ` (${h.worker_employee_id})` : ''}</td>
      )}
      <td style={{ ...s.tdDesc, ...(showPeople ? null : firstBorder) }} title={h.job_profile}>{h.job_profile || '—'}</td>
      <td style={tbl.td}>{h.manager || '—'}</td>
      {showPeople && (
        <td style={tbl.td}>
          {canEditThisRow ? (
            <input
              type="text" style={s.editInput}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              onBlur={savePhoneNumber}
            />
          ) : (h.phone_number || '—')}
        </td>
      )}
      <td style={tbl.td}>
        {h.job_requisition_category
          ? <span style={{ ...s.badge, background: catC.bg, color: catC.color }}>{h.job_requisition_category}</span>
          : '—'}
      </td>
      <td style={tbl.td}>{fmtDate(h.start_date)}</td>
      <CheckCell checked={h.ninety_day} editable={canEditThisRow} onToggle={toggleNinetyDay} />
      <CheckCell checked={h.pre_hire} editable={canEditThisRow} onToggle={togglePreHire} />
      <WeekCell checked={h.week1} />
      <WeekCell checked={h.week2} />
      <WeekCell checked={h.week3} />
      <WeekCell checked={h.week4} />
      <td style={s.tdNotes}>
        <NotesCell value={notes} onChange={setNotes} onBlur={saveNotes} canEdit={canEditThisRow} />
      </td>
      <td style={tbl.td}>
        {canEditThisRow ? (
          <select style={s.select} value={language} onChange={(e) => saveLanguage(e.target.value)}>
            <option value="">—</option>
            <option value="English">English</option>
            <option value="Spanish">Spanish</option>
          </select>
        ) : (h.language || '—')}
      </td>
    </tr>
  );
}

function WeekCell({ checked }) {
  return (
    <td style={s.tdCenter}>
      {checked ? <Check size={14} color={c.success} /> : <span style={s.dash}>—</span>}
    </td>
  );
}

// Pre-Hire / 90 Day: a real checkbox Recruiting ticks by hand, unlike the
// read-only, date-calculated WeekCell.
function CheckCell({ checked, editable, onToggle }) {
  return (
    <td style={s.tdCenter}>
      {editable ? (
        <input type="checkbox" checked={!!checked} onChange={onToggle} style={s.checkbox} />
      ) : (
        checked ? <Check size={14} color={c.success} /> : <span style={s.dash}>—</span>
      )}
    </td>
  );
}

const s = {
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: t.bodySm.fontSize },
  tdCenter:    { ...tbl.td, textAlign: 'center' },
  tdDesc:      { ...tbl.td, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tdNotes:     { ...tbl.td, minWidth: '180px' },
  dash:        { color: c.borderStrong },
  empty:       { textAlign: 'center', padding: '24px', color: c.textFaint },
  editInput:   { width: '100%', minWidth: '110px', padding: '4px 6px', border: `1px solid ${c.borderStrong}`, borderRadius: '4px', fontSize: t.caption.fontSize, fontFamily: 'inherit' },
  select:      { padding: '4px 6px', border: `1px solid ${c.borderStrong}`, borderRadius: '4px', fontSize: t.caption.fontSize, fontFamily: 'inherit' },
  checkbox:    { width: '15px', height: '15px', cursor: 'pointer' },
  badge:       { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: t.micro.fontSize, fontWeight: 600, whiteSpace: 'nowrap' },
};
